/**
 * Styles for Process components
 * Centralizing styles improves maintainability and keeps component logic clean
 */

/**
 * Main styles for the ProcessVisualizer component
 * @param {boolean} isDarkTheme - Whether dark theme is active
 * @returns {Object} Style object for the component
 */
export const getProcessVisualizerStyles = (isDarkTheme) => ({
  container: {
    width: '100%', 
    p: 2,
    padding: 0
  },
  paper: {
    width: '100%', 
    mb: 2,
    backgroundColor: isDarkTheme ? 'rgba(30, 30, 30, 0.9)' : undefined,
    color: isDarkTheme ? 'rgba(255, 255, 255, 0.9)' : undefined,
    '& .MuiTableCell-root': {
      color: isDarkTheme ? 'rgba(255, 255, 255, 0.9)' : undefined,
      borderBottomColor: isDarkTheme ? 'rgba(255, 255, 255, 0.1)' : undefined
    },
    '& .MuiTableHead-root .MuiTableCell-root': {
      color: isDarkTheme ? 'rgba(255, 255, 255, 0.7)' : undefined,
      fontWeight: 'bold'
    },
    '& .MuiTableSortLabel-root': {
      color: isDarkTheme ? 'rgba(255, 255, 255, 0.7) !important' : undefined,
    },
    '& .MuiTableSortLabel-root.Mui-active': {
      color: isDarkTheme ? 'rgba(144, 202, 249, 0.9) !important' : undefined,
    },
    '& .MuiTableSortLabel-icon': {
      color: isDarkTheme ? 'rgba(144, 202, 249, 0.9) !important' : undefined,
    },
    '& .MuiTableRow-root:hover': {
      backgroundColor: isDarkTheme ? 'rgba(255, 255, 255, 0.08) !important' : undefined,
    },
    '& .MuiTableRow-root.Mui-selected': {
      backgroundColor: isDarkTheme ? 'rgba(144, 202, 249, 0.16) !important' : undefined,
    }
  },
  toolbarContainer: {
    display: 'flex', 
    alignItems: 'center', 
    p: 2, 
    gap: 2
  },
  searchField: {
    flexGrow: 1,
    '& .MuiOutlinedInput-root': {
      '& fieldset': {
        borderColor: isDarkTheme ? 'rgba(255, 255, 255, 0.23)' : undefined,
      },
      '&:hover fieldset': {
        borderColor: isDarkTheme ? 'rgba(255, 255, 255, 0.5)' : undefined,
      },
    },
    '& .MuiInputLabel-root': {
      color: isDarkTheme ? 'rgba(255, 255, 255, 0.7)' : undefined,
    },
    '& .MuiInputBase-input': {
      color: isDarkTheme ? 'rgba(255, 255, 255, 0.9)' : undefined,
    },
  },
  statusFilter: {
    minWidth: 120,
    '& .MuiOutlinedInput-root': {
      '& fieldset': {
        borderColor: isDarkTheme ? 'rgba(255, 255, 255, 0.8)' : undefined,
      },
      '&:hover fieldset': {
        borderColor: isDarkTheme ? 'rgba(255, 255, 255, 0.5)' : undefined,
      },
    },
    '& .MuiInputLabel-root': {
      color: isDarkTheme ? 'rgba(255, 255, 255, 0.7)' : undefined,
    },
    '& .MuiSelect-select': {
      color: isDarkTheme ? 'rgba(255, 255, 255, 0.9)' : undefined,
    },
    '& .MuiSvgIcon-root': {
      color: isDarkTheme ? 'rgba(255, 255, 255, 0.7)' : undefined,
    }
  },
  menuProps: {
    PaperProps: {
      sx: {
        bgcolor: isDarkTheme ? 'rgb(45, 45, 45)' : undefined,
        '& .MuiMenuItem-root': {
          color: isDarkTheme ? 'rgba(255, 255, 255, 0.9)' : undefined,
        },
        '& .MuiMenuItem-root:hover': {
          backgroundColor: isDarkTheme ? 'rgba(255, 255, 255, 0.08)' : undefined,
        },
        '& .MuiMenuItem-root.Mui-selected': {
          backgroundColor: isDarkTheme ? 'rgba(144, 202, 249, 0.16)' : undefined,
        }
      }
    }
  }
});

/**
 * Styles for ProcessRow component
 * @param {boolean} isDarkTheme - Whether dark theme is active
 * @param {boolean} isActive - Whether the process is active
 * @returns {Object} Style objects for the row component
 */
export const getProcessRowStyles = (isDarkTheme, isActive) => ({
  row: {
    '& > *': { borderBottom: 'unset' },
  },
  statusIndicator: {
    display: 'inline-block',
    width: '10px',
    height: '10px',
    borderRadius: '50%',
    marginRight: '8px',
    backgroundColor: isActive ? '#4caf50' : '#f44336', // Green for active, red for inactive
    boxShadow: `0 0 4px ${isActive ? '#4caf50' : '#f44336'}`,
  },
  expandIcon: {
    color: isDarkTheme ? '#eee' : '#444',
  },
  alertButton: {
    marginLeft: '8px',
    color: isDarkTheme ? 'rgba(144, 202, 249, 0.9)' : 'rgba(33, 108, 209, 0.9)',
    backgroundColor: isDarkTheme ? 'rgba(30, 30, 30, 0.6)' : 'rgba(240, 242, 245, 0.8)',
    padding: '4px',
    borderRadius: '4px',
    '&:hover': {
      backgroundColor: isDarkTheme ? 'rgba(57, 138, 255, 0.2)' : 'rgba(33, 108, 209, 0.1)',
      color: isDarkTheme ? 'rgba(144, 202, 249, 1)' : 'rgba(33, 108, 209, 1)',
    },
  },
  detailsContainer: {
    margin: 1, 
    padding: 2, 
    backgroundColor: isDarkTheme ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)', 
    borderRadius: '4px'
  }
});
